import { routeCobblesManager } from "@/setup/routes/mill.routes";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import {
	Checkbox,
	Divider,
	FormControlLabel,
	Grid2,
	Paper,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GridApi } from "ag-grid-community";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { CobblesManagerTable } from "./components/CobblesManagerTable";
import { Cobble } from "./models/Cobble";
import { getCobbles } from "./repositories";

type useCobblesProps = {
	shiftId: number | undefined;
	includeCorrections: boolean;
	onError: (error: Error) => void;
};

export const useCobbles = ({
	shiftId,
	includeCorrections,
	onError,
}: useCobblesProps) => {
	return useQuery<Cobble[], Error>({
		queryKey: ["cobbles", shiftId, includeCorrections],
		queryFn: ({ signal }) =>
			getCobbles({ shiftId: shiftId || 0, includeCorrections }, signal),
		onError: onError,
		enabled: !!shiftId,
	});
};

export const CobblesManagerPage = () => {
	const [error, setError] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const includeCorrections = params.get("includeCorrections") === "true";
	const cobbleId = params.get("cobbleId");

	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const { data: cobbles, isLoading } = useCobbles({
		shiftId: shiftInfo?.CurrentShiftId,
		includeCorrections: includeCorrections,
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			const url = buildURL(
				shiftId || Number(shiftIdParam),
				cobbleId || undefined,
			);
			navigate(url, { replace: true });
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (shiftIdParam) {
			(async () => {
				const shiftId = Number(shiftIdParam);
				await getShiftDataFromAPI(null, shiftId);
			})();
		} else {
			(async () => {
				await getShiftDataFromAPI(null, 0);
			})();
		}
	}, []);

	useEffect(() => {
		if (!isLoading && cobbleId) {
			const cobbleNode = gridApi?.getRowNode(cobbleId);
			if (cobbleNode) {
				gridApi?.ensureNodeVisible(cobbleNode);
				cobbleNode.setSelected(true);
			}
		}
	}, [isLoading, cobbleId]);

	const buildURL = (
		shiftId: string | number,
		newCobbleId?: string,
		newIncludeCorrections?: boolean,
	) => {
		const queryParams = new URLSearchParams();

		if (newCobbleId) {
			queryParams.set("cobbleId", newCobbleId);
		}

		const corrections =
			newIncludeCorrections !== undefined
				? newIncludeCorrections
				: includeCorrections;
		if (corrections) {
			queryParams.set("includeCorrections", "true");
		}

		const queryString = queryParams.toString();
		return `${routeCobblesManager}/${shiftId}${
			queryString ? `?${queryString}` : ""
		}`;
	};

	const onRowClicked = (cobble: Cobble | null) => {
		if (cobble) {
			const currentShiftId = shiftInfo?.CurrentShiftId || Number(shiftIdParam);
			const url = buildURL(currentShiftId, cobble.BilletId.toString());
			navigate(url, { replace: true });
		}
	};

	const handleCheckboxChange = (checked: boolean) => {
		const currentShiftId = shiftInfo?.CurrentShiftId || Number(shiftIdParam);
		const url = buildURL(currentShiftId, cobbleId || undefined, checked);
		navigate(url, { replace: true });
	};

	return (
		<>
			<HelmetDexteel title="Cobbles Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				sx={{
					backgroundColor: pageBgColor,
					minHeight: "calc(100vh - 160px)",
				}}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Cobbles Manager
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Grid2
						container
						justifyContent="flex-start"
						alignItems="center"
						spacing={2}
					>
						<Grid2 maxWidth={"fit-content"}>
							<ShiftDayNavigatorControl preventInit />
						</Grid2>
						<Grid2 size={{ xs: 4 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={includeCorrections}
										color="primary"
										onChange={(event) =>
											handleCheckboxChange(event.target.checked)
										}
									/>
								}
								label="Show corrections"
							/>
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2>
					<Divider />
				</Grid2>
				<Grid2
					size={{ md: 12 }}
					sx={{
						height: "70vh",
						minHeight: "70vh",
					}}
					component={Paper}
					elevation={2}
				>
					<CobblesManagerTable
						rows={cobbles || []}
						isLoading={isLoading}
						onRowClicked={onRowClicked}
						onGridReady={(api) => setGridApi(api)}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
